import * as React from "react";
import * as SelectPrimitive from "@radix-ui/react-select";
import { Check, ChevronDown, Loader2 } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip";

export interface DropdownOption {
  value: string;
  label: string;
  /** Optional dot color class (e.g. `bg-emerald-500`) shown before the label. */
  dot?: string;
  /** Optional icon rendered before the label. */
  icon?: React.ReactNode;
  disabled?: boolean;
}

interface DropdownProps {
  value: string;
  onChange: (value: string) => void;
  options: DropdownOption[];
  placeholder?: string;
  showPlaceholderWhenEmpty?: boolean;
  /** Renders a small label chip inside the trigger before the value. */
  leadingLabel?: string;
  /** Width of the trigger. Defaults to `min-w-[160px]`. */
  className?: string;
  disabled?: boolean;
  /** Trigger height. */
  size?: "sm" | "md";
  align?: "start" | "center" | "end";
  /**
   * Visual style of the trigger.
   * - `pill` (default): rounded card-style pill — best for filter bars / toolbars.
   * - `form`: matches the in-modal form-input look (subtle bg, full-width).
   */
  variant?: "pill" | "form";
  /** Shows a search box at the top of the option list — for long lists
   * (e.g. a country picker) where scrolling to find an entry is slow. */
  searchable?: boolean;
  /** Renders a checkbox-style square before each option instead of the default
   *  trailing checkmark. Purely visual — selection stays single-choice. */
  checkboxStyle?: boolean;
  /** Options are still being fetched — spins the trigger icon and shows a
   *  loading row in the option list instead of an empty menu. */
  loading?: boolean;
  /** Lazy loading: how many options to render at a time. The list grows as
   *  the user scrolls, so a long list (hundreds of apps) stays cheap to open. */
  chunkSize?: number;
}

const SIZE_CLASS = {
  sm: "h-[38px] text-sm px-3",
  md: "h-[38px] text-sm px-3.5",
} as const;

const VARIANT_CLASS = {
  pill: "rounded-lg border border-border bg-card text-foreground hover:border-indigo-400/60 dark:hover:border-indigo-400/40 focus-visible:border-indigo-500 dark:focus-visible:border-indigo-500 focus-visible:ring-2 focus-visible:ring-indigo-500/20",
  form: "rounded-lg border border-[#e2e8f0] dark:border-white/[0.07] bg-[#f5f7fb] dark:bg-white/[0.04] text-[#0f172a] dark:text-white hover:border-indigo-400/60 dark:hover:border-indigo-400/40 focus-visible:border-indigo-500 dark:focus-visible:border-indigo-500 focus-visible:ring-2 focus-visible:ring-indigo-500/10 w-full",
} as const;

/**
 * Single dropdown component used everywhere in the app.
 *
 * Looks identical in light & dark themes via shadcn/Tailwind tokens
 * (`bg-card`, `border-border`, `text-foreground`, `bg-popover`, etc.).
 *
 * Built on Radix Select so it gets keyboard navigation, focus trap,
 * portal positioning, and a11y for free.
 */
/**
 * Radix Select forbids `value=""` on items (it reserves "" for "no selection").
 * Many call sites use `""` to mean "All / Any" AS A SELECTABLE OPTION. We map
 * empty string ↔ this sentinel token internally so callers can keep using `""`.
 *
 * If the consumer's value is `""` but NO option uses `""`, treat it as
 * "unselected" and let Radix show the placeholder.
 */
const EMPTY_VALUE_TOKEN = "__dropdown_empty__";
const fromRadix = (v: string) => (v === EMPTY_VALUE_TOKEN ? "" : v);

/** Selected value that reveals the full text in a themed tooltip — only when
 *  truncated. `className` lets callers keep their flex/truncate layout (the
 *  wrapper should stay `truncate` so overflow is detectable). */
export function SelectedLabel({
  label,
  className = "truncate",
}: {
  label: string;
  className?: string;
}) {
  const ref = React.useRef<HTMLSpanElement>(null);
  const [open, setOpen] = React.useState(false);
  const isOverflowing = () => {
    const el = ref.current;
    return !!el && el.scrollWidth > el.clientWidth + 1;
  };
  return (
    <Tooltip
      open={open}
      onOpenChange={(next) => setOpen(next ? isOverflowing() : false)}
    >
      <TooltipTrigger asChild>
        <span ref={ref} className={className}>
          {label}
        </span>
      </TooltipTrigger>
      <TooltipContent side="top" sideOffset={6} className="max-w-xs break-words">
        {label}
      </TooltipContent>
    </Tooltip>
  );
}

export function Dropdown({
  value,
  onChange,
  options,
  placeholder = "Select…",
  showPlaceholderWhenEmpty = false,
  leadingLabel,
  className = "",
  disabled,
  size = "md",
  align = "start",
  variant = "pill",
  searchable = false,
  checkboxStyle = false,
  loading = false,
  chunkSize = 50,
}: DropdownProps) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const filteredOptions = React.useMemo(() => {
    const q = searchable ? query.trim().toLowerCase() : "";
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query, searchable]);

  const [visibleCount, setVisibleCount] = React.useState(chunkSize);
  React.useEffect(() => {
    setVisibleCount(chunkSize);
  }, [query, chunkSize, options.length]);
  const selectedIndex = React.useMemo(
    () => filteredOptions.findIndex((o) => o.value === value),
    [filteredOptions, value],
  );
  const renderCount = Math.max(visibleCount, selectedIndex + 1);
  const visibleOptions = React.useMemo(
    () => filteredOptions.slice(0, renderCount),
    [filteredOptions, renderCount],
  );
  const hasMore = renderCount < filteredOptions.length;
  const handleListScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (!hasMore) return;
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 48) {
      setVisibleCount((n) => n + chunkSize);
    }
  };
  const hasEmptyOption = React.useMemo(
    () => options.some((o) => o.value === ""),
    [options],
  );
  const selected = React.useMemo(
    () => options.find((o) => o.value === value),
    [options, value],
  );
  const showEmptyAsPlaceholder = showPlaceholderWhenEmpty && value === "";
  const radixValue =
    value === ""
      ? hasEmptyOption && !showEmptyAsPlaceholder
        ? EMPTY_VALUE_TOKEN
        : undefined
      : value;

  return (
    <SelectPrimitive.Root
      value={radixValue}
      onValueChange={(v) => onChange(fromRadix(v))}
      disabled={disabled}
      open={searchable ? open : undefined}
      onOpenChange={
        searchable
          ? (next) => {
              setOpen(next);
              if (!next) setQuery("");
            }
          : undefined
      }
    >
      <SelectPrimitive.Trigger
        className={`group inline-flex min-w-[160px] items-center justify-between gap-2 outline-none transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer data-[placeholder]:d ${VARIANT_CLASS[variant]} ${SIZE_CLASS[size]} ${className}`}
      >
        <span className="flex items-center gap-2 min-w-0 flex-1">
          {leadingLabel && (
            /* Same size as the value beside it — only the colour sets it apart. */
            <span className="text-sm d shrink-0">{leadingLabel}</span>
          )}
          {!showEmptyAsPlaceholder && selected?.icon}
          {selected && !showEmptyAsPlaceholder ? (
            <SelectedLabel label={selected.label} />
          ) : (
            <SelectPrimitive.Value placeholder={placeholder} />
          )}
        </span>
        <SelectPrimitive.Icon asChild>
          {loading ? (
            <Loader2 className="size-4 d animate-spin" />
          ) : (
            <ChevronDown className="size-4 d transition-transform duration-200 group-data-[state=open]:rotate-180" />
          )}
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>

      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          position="popper"
          sideOffset={6}
          align={align}
          className="z-[200] flex flex-col min-w-[var(--radix-select-trigger-width)] w-max max-w-[min(22rem,calc(100vw-1rem))] max-h-[320px] overflow-hidden rounded-xl border border-border bg-popover text-popover-foreground shadow-xl will-change-transform data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=open]:duration-150 data-[state=closed]:duration-100"
        >
          {searchable && (
            <div className="p-1.5 border-b border-border shrink-0">
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.stopPropagation()}
                placeholder="Search…"
                className="w-full text-sm bg-[#f5f7fb] dark:bg-white/[0.04] rounded-md px-2.5 py-1.5 outline-none border border-[#e2e8f0] dark:border-white/[0.07] focus:border-indigo-500 dark:focus:border-indigo-500 text-[#0f172a] dark:text-white placeholder:text-[#64748b] dark:placeholder:text-white/25"
              />
            </div>
          )}
          <SelectPrimitive.Viewport asChild>
            <div
              onScroll={handleListScroll}
              className="p-1 flex-1 min-h-0 max-h-[260px] overflow-y-auto [scrollbar-width:thin] [scrollbar-color:rgba(100,116,139,0.7)_rgba(148,163,184,0.18)] [&::-webkit-scrollbar]:w-[10px] [&::-webkit-scrollbar-track]:bg-slate-400/15 [&::-webkit-scrollbar-track]:rounded-full [&::-webkit-scrollbar-track]:my-1 [&::-webkit-scrollbar-thumb]:bg-slate-400/70 dark:[&::-webkit-scrollbar-thumb]:bg-slate-500/70 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:border-2 [&::-webkit-scrollbar-thumb]:border-solid [&::-webkit-scrollbar-thumb]:border-transparent [&::-webkit-scrollbar-thumb]:bg-clip-content [&::-webkit-scrollbar-thumb]:hover:bg-slate-500 dark:[&::-webkit-scrollbar-thumb]:hover:bg-slate-400">
              {loading ? (
                <div className="flex items-center justify-center gap-2 px-2.5 py-3 text-sm d">
                  <Loader2 className="size-4 animate-spin" />
                  Loading
                </div>
              ) : searchable && filteredOptions.length === 0 ? (
                <div className="px-2.5 py-3 text-sm text-center d">No matches</div>
              ) : (
                visibleOptions.map((opt) => (
                <SelectPrimitive.Item
                  key={opt.value}
                  value={opt.value === "" ? EMPTY_VALUE_TOKEN : opt.value}
                  disabled={opt.disabled}
                  className="group/item relative flex w-full cursor-pointer select-none items-center gap-2.5 rounded-lg px-2.5 py-1.5 pr-8 text-sm text-foreground outline-none transition-colors break-words data-[highlighted]:bg-indigo-50 dark:data-[highlighted]:bg-indigo-500/10 data-[highlighted]:text-indigo-700 dark:data-[highlighted]:text-indigo-300 data-[state=checked]:font-semibold data-[disabled]:opacity-40 data-[disabled]:cursor-not-allowed"
                >
                  {checkboxStyle && (
                    <span
                      className={`flex size-4 shrink-0 items-center justify-center rounded border-2 transition-colors ${
                        opt.value === value
                          ? "border-indigo-500 bg-indigo-500 text-white"
                          : "border-slate-400 dark:border-white/40"
                      }`}
                    >
                      {opt.value === value && <Check className="size-3" />}
                    </span>
                  )}
                  {opt.icon}
                  <SelectPrimitive.ItemText>{opt.label}</SelectPrimitive.ItemText>
                  {!checkboxStyle && (
                    <SelectPrimitive.ItemIndicator className="ml-auto flex-shrink-0">
                      <Check className="size-4 text-indigo-600 dark:text-indigo-400" />
                    </SelectPrimitive.ItemIndicator>
                  )}
                </SelectPrimitive.Item>
                ))
              )}
              {/* Sentinel for the next chunk — scrolling it into view is
                  what triggers handleListScroll to mount more options. */}
              {!loading && hasMore && (
                <div className="flex items-center justify-center gap-2 px-2.5 py-2 text-xs d">
                  <Loader2 className="size-3 animate-spin" />
                  Loading more
                </div>
              )}
            </div>
          </SelectPrimitive.Viewport>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  );
}
